import API from './api.js';
import { getAuthCookie, removeAuthCookie } from '../utils/cookies.js';

class DataController {
    user = null;
    context = null;
    aba = null;

    /**
     * Get the API instance
     * @returns {API}
     */
    get API() {
        return API.Instance();
    }

    async authenticate() {
        try {
            const token = getAuthCookie();
            if (token === 'expired') return false;
            if (this.user) return true;
            const user = await this.fetchUserByToken();
            if (user) {
                this.user = user;
                this.aba = user?.access[0];
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error in authenticate', error);
            return false;
        }
    }

    async fetchUserByToken() {
        try {
            if (this.user) return this.user;

            const token = getAuthCookie();
            if (token === 'expired') return {};

            const user = await this.API.requestUser(token);
            if (user === 401) return {};

            if (!user?.data?.access[0]) return {access: false};

            const fi = await this.fetchFI(user?.data?.access[0]);
            const userData = { ...user?.data, fi: fi || {} };
            this.user = userData;
            this.aba = user?.data?.access[0];
            return userData;
        } catch (error) {
            console.error('Error in fetchUserByToken', error);
            removeAuthCookie();
            window.location.href = '/';
            return {};
        }
    }

    async loginUser(payload) {
        try {
            const response = await this.API.requestAuthenticate(payload);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in loginUser', error);
            return {};
        }
    }

    async fetchReport(aba, start, end, grouping) {
        try {
            const response = await this.API.requestReport(aba || this.aba, start, end, grouping);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchReport', error);
            return {};
        }
    }

    async fetchFI(aba) {
        try {
            const response = await this.API.requestFI(aba || this.aba);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchFI', error);
            return {};
        }
    }

    async fetchUsers(filters = {}) {
        try {
            const response = await this.API.requestUsers(this.aba, filters);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchUsers', error);
            return {};
        }
    }

    async fetchAdminUsers(aba) {
        try {
            const response = await this.API.requestAdminUsers(aba || this.aba);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchAdminUsers', error);
            return {};
        }
    }

    async fetchLedger() {
        try {
            const response = await this.API.requestLedger(this.aba);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchLedger', error);
            return {};
        }
    }

    async updateFI(aba, payload) {
        try {
            const response = await this.API.requestUpdateFI(aba || this.aba, payload);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in updateFI', error);
            return {};
        }
    }

    async fetchYTDReport(aba) {
        try {
            const response = await this.API.requestYTDReport(aba || this.aba);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchYTDReport', error);
            return {};
        }
    }

    async fetchConsumerUser(clientId) {
        try {
            const response = await this.API.requestConsumerUser(clientId);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchConsumerUser', error);
            return {};
        }
    }

    async fetchReconciliation() {
        try {
            const response = await this.API.requestReconciliation(this.aba);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchReconciliation', error);
            return {};
        }
    }

    async fetchTreasurySummary(startDate, endDate) {
        try {
            const response = await this.API.requestTreasurySummary(this.aba, startDate, endDate);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchTreasurySummary', error);
            return {};
        }
    }

    async fetchTreasuryFunding(startDate, endDate) {
        try {
            const response = await this.API.requestTreasuryFunding(this.aba, startDate, endDate);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchTreasuryFunding', error);
            return {};
        }
    }

    async fetchTreasuryKPIs(startDate, endDate) {
        try {
            const response = await this.API.requestTreasuryKPIs(this.aba, startDate, endDate);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchTreasuryKPIs', error);
            return {};
        }
    }

    async fetchTreasuryReceivables(startDate, endDate) {
        try {
            const response = await this.API.requestTreasuryReceivables(this.aba, startDate, endDate);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchTreasuryReceivables', error);
            return {};
        }
    }

    async fetchTreasuryActivities(startDate, endDate) {
        try {
            const response = await this.API.requestTreasuryActivities(this.aba, startDate, endDate);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchTreasuryActivities', error);
            return {};
        }
    }

    async fetchTreasuryCashflow(startDate, endDate) {
        try {
            const response = await this.API.requestTreasuryCashflow(this.aba, startDate, endDate);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchTreasuryCashflow', error);
            return {};
        }
    }

    async removeUserAccess(userId) {
        try {
            const response = await this.API.requestRemoveUserAccess(this.aba, userId);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in removeUserAccess', error);
            throw error;
        }
    }

    async sendInvitation(payload) {
        try {
            // For send invitation, we only send email and aba
            const { email } = payload;
            if (!email) {
                throw new Error('Email is required for sending invitation');
            }

            const payloadWithAba = { email, aba: this.aba };
            const response = await this.API.requestSendInvitation(payloadWithAba);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in sendInvitation', error);
            throw error;
        }
    }

    async validateInvitation(token) {
        try {
            const response = await this.API.requestValidateInvitation(token);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in validateInvitation', error);
            throw error;
        }
    }

    async validatePasswordReset(token) {
        try {
            const response = await this.API.requestValidatePasswordReset(token);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in validatePasswordReset', error);
            throw error;
        }
    }

    async updatePassword(payload) {
        try {
            const response = await this.API.requestUpdatePassword(payload);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in updatePassword', error);
            throw error;
        }
    }

    async registerUser(payload) {
        try {
            // Transform payload to match API expected format
            const { firstName, lastName, email, password, phoneNumber, } = payload;

            const registerPayload = {
                name: `${firstName} ${lastName}`.trim(),
                email,
                password
            };

            if (phoneNumber) {
                registerPayload.phone = phoneNumber;
            }

            const response = await this.API.requestRegister(registerPayload);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in registerUser', error);
            throw error;
        }
    }

    async updateAccount(payload) {
        try {
            const response = await this.API.requestUpdateAccount(payload);
            this.context = response;
            
            // Update local user data if successful
            if (response?.data) {
                this.user = { ...this.user, ...response.data };
            }
            
            return response?.data;
        } catch (error) {
            console.error('Error in updateAccount', error);
            throw error;
        }
    }

    async sendPasswordReset(email) {
        try {
            if (!email) {
                throw new Error('Email is required for password reset');
            }

            const payload = { email };
            const response = await this.API.requestSendPasswordReset(this.aba, payload);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in sendPasswordReset', error);
            throw error;
        }
    }

    async fetchBillingKPIs(startDate, endDate) {
        try {
            const response = await this.API.requestBillingKPIs(this.aba, startDate, endDate);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchBillingKPIs', error);
            return {};
        }
    }

    async fetchBillingStatements(startDate, endDate) {
        try {
            const response = await this.API.requestBillingStatements(this.aba, startDate, endDate);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchBillingStatements', error);
            return [];
        }
    }

    async exportBillingStatements(statementIds) {
        try {
            const payload = { statementIds };
            const response = await this.API.requestExportBillingStatements(this.aba, payload);

            // Create a download link
            if (response?.data) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `billing_statements_${new Date().toISOString().split('T')[0]}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }

            return response?.data;
        } catch (error) {
            console.error('Error in exportBillingStatements', error);
            throw error;
        }
    }

    async fetchSyncMonitor(status = null, limit = 50) {
        try {
            const response = await this.API.requestSyncMonitor(this.aba, status, limit);
            this.context = response;
            return response?.data;
        } 
        catch (error) {
            console.error('Error in fetchSyncMonitor', error);
            return { syncs: [] };
        }
    }

    async sendSyncNotification(clientId, notificationType, linkTokenId) {
        try {
            const payload = {
                client_id: clientId,
                notification_type: notificationType,
                link_token_id: linkTokenId
            };
            const response = await this.API.requestSendSyncNotification(this.aba, payload);
            this.context = response;
            return response?.data;
        } 
        catch (error) {
            console.error('Error in sendSyncNotification', error);
            throw error;
        }
    }

    async resolveTroubleTicket(ticketId, sendNotification = false) {
        try {
            const payload = {
                send_notification: sendNotification,
                aba: this.aba
            };
            const response = await this.API.requestResolveTroubleTicket(ticketId, payload);
            this.context = response;
            return response?.data;
        } 
        catch (error) {
            console.error('Error in resolveTroubleTicket', error);
            throw error;
        }
    }

    async fetchPlatforms(provider = null, search = null) {
        try {
            const response = await this.API.requestPlatforms(this.aba, provider, search);
            this.context = response;
            return response?.data;
        } 
        catch (error) {
            console.error('Error in fetchPlatforms', error);
            return { platforms: [] };
        }
    }

    async fetchPlatformOutages(platformId = null, provider = null, status = 'active') {
        try {
            const response = await this.API.requestPlatformOutages(this.aba, platformId, provider, status);
            this.context = response;
            return response?.data;
        } 
        catch (error) {
            console.error('Error in fetchPlatformOutages', error);
            return { outages: [] };
        }
    }

    async createPlatformOutage(payload) {
        try {
            const response = await this.API.requestCreatePlatformOutage(this.aba, payload);
            this.context = response;
            return response?.data;
        } 
        catch (error) {
            console.error('Error in createPlatformOutage', error);
            throw error;
        }
    }

    async updatePlatformOutage(outageId, payload) {
        try {
            const response = await this.API.requestUpdatePlatformOutage(this.aba, outageId, payload);
            this.context = response;
            return response?.data;
        } 
        catch (error) {
            console.error('Error in updatePlatformOutage', error);
            throw error;
        }
    }

    async resolvePlatformOutage(outageId) {
        try {
            const response = await this.API.requestResolvePlatformOutage(this.aba, outageId);
            this.context = response;
            return response?.data;
        } 
        catch (error) {
            console.error('Error in resolvePlatformOutage', error);
            throw error;
        }
    }
}

export default DataController;
